import { BaseAuditModel } from './base.models';
import { GenericError } from './error.models';
import { RecordType } from './record.models';
import { SearchResultResponse } from 'store/models/screening.models';

export enum SearchEngineTypeLabel {
  ARTEMISCAN = 'ARTEMISCAN',
  INTERNET_SEARCH_2 = 'INTERNET SEARCH 2',
  INTERNET_SEARCH = 'INTERNET SEARCH',
  OWN_NAME = 'OWN RESTRICTED LIST',
  DJ = 'DOW JONES FEED',
  DJ2 = 'DOW JONES API',
  DJ3 = 'DOW JONES',
  TR = 'WORLD-CHECK'
}

export enum SearchEngineTypeValue {
  ARTEMISCAN = 'ArtemiScan',
  INTERNET_SEARCH = 'Internet2',
  OWN_NAME = 'OwnName',
  DJ = 'DJ',
  DJ2 = 'DJ2',
  DJ3 = 'DJ3',
  TR = 'TR',
  NLP = 'NLP'
}

export const SearchEngineLabel = {
  [SearchEngineTypeValue.ARTEMISCAN]: 'ARTEMISCAN',
  [SearchEngineTypeValue.INTERNET_SEARCH]: 'INTERNET SEARCH',
  [SearchEngineTypeValue.OWN_NAME]: 'OWN RESTRICTED LIST',
  [SearchEngineTypeValue.DJ]: 'DOW JONES FEED',
  [SearchEngineTypeValue.DJ2]: 'DOW JONES API',
  [SearchEngineTypeValue.DJ3]: 'DOW JONES',
  [SearchEngineTypeValue.TR]: 'WORLD-CHECK',
};

export interface SearchEngine {
  label: SearchEngineTypeLabel;
  value: SearchEngineTypeValue;
  isChecked?: boolean;
}

export enum SearchType {
  QUICKSCAN = 'QUICKSCAN',
  SCREENING = 'SCREENING',
  ODD = 'ODD',
}

interface DJNameDetail {
  firstName: string;
  middleName: string;
  originalScriptName: string;
  singleStringName: string;
  suffix: string;
  surname: string;
  titleHonorific: string;
  type: string;
}

interface CountryDetail {
  country: string;
  type: string;
}

interface Description {
  description1: string;
  description2: string;
  description3?: string;
}

interface SubsInfo extends BaseAuditModel {
  domains: number[];
  sublistName: string;
}

interface OwnName extends BaseAuditModel {
  active: boolean;
  comment: string;
  country: string;
  fullName: string;
  idNumber: string;
  ownSublist: number;
  ownSublistInfo: SubsInfo;
  uniqueReferenceCode: string;
}

export interface SearchItem extends BaseAuditModel {
  // FIXME: Later on talked to BE guys to use:
  // record vs qrecord => just one
  // engineVersion vs version => just one
  // searchResults vs internetSearchResults => just searchResults
  record: number;
  qrecord?: number;
  engineType: SearchEngineTypeValue;
  engineVersion: string;
  version?: string;
  error?: string;
  name?: string;
  invalidReason: string;
  isPeriodic?: boolean;
  isSearchFinished?: boolean;
  domain?: string;
  recordType: RecordType;
  searchResults?: ResultItem[];
  internetSearchResults?: ResultItem[];
  searchText: string;
  valid: boolean;
}

export interface ResultItem extends BaseAuditModel {
  comment: ScreeningResultComment;
  dialog?: number;
  matched?: boolean;
  search?: number;
  uniqueReferenceCode?: string;
  searchResponse?: SearchResponse;
  // For ownName search only
  ownName?: OwnName;
  ownNameSearch?: number;
  // For internet search only
  internetSearch?: number;
  link?: string;
  summary?: string;
  title?: string;
  // for dj3 search only
  customerHash?: string;
  matchId?: string;
}

interface SearchResponse {
  // Common
  deceased: boolean;
  nationality: string;
  uniqueReferenceCode: string;
  updatedAt: string;
  updatedBy: Account;
  // For artemis
  concatAl: any;
  formattedName: string;
  issueDate: string;
  link: string;
  name: string;
  order: string;
  parse: string;
  source: string;
  yob: string;
  // For DJ
  activeStatus: string;
  address: string[];
  adverseMedia: boolean;
  associations: any;
  countryDetail: CountryDetail[];
  dateDetail: any;
  dateOfBirth: string;
  descriptions: Description[];
  djId: string;
  gender: string;
  idNumberDetail: any[];
  images: any[];
  modifiedOn: string;
  DJnameDetail: DJNameDetail[];
  pep: boolean;
  placeOfBirth: string;
  primaryName: string;
  profileNotes: string;
  roles: any[];
  sanction: boolean;
  sanctionsReferences: any[];
  sourceDescription: string;
  // For DJ2
  score: string;
  associates: any[];
  countries: CountryDetail[];
  address_details: any[];
  birth_place_details: any[];
  country_details: any[];
  date_details: any[];
  dates_of_birth: any[];
  dj_id: string;
  identification_details: any[];
  image_details: any[];
  match_type: string;
  matched_name: string;
  watchlist_content: any;
  person_name_details: any[];
  primary_name: string;
  risk_icons: any[];
  state_owned_companies_content: any[];
  // For DJ3
  customerHash?: string;
  matchId?: string;
  customerDetail?: any;
}

export interface SearchItemError extends Partial<SearchItem> {
  error: string;
}

export type DatabaseSearch = {
  [K in SearchEngineTypeValue]?: SearchResultResponse | SearchItem;
};

export interface Screening extends DatabaseSearch {
  isScreening?: boolean;
}

export interface QuickScan extends DatabaseSearch {
  isQuickScan?: boolean;
}

export interface QuickScanSearch extends SearchResultResponse {
  engineType: SearchEngineTypeValue;
  searchType: SearchType;
}

export interface OngoingDueDiligence extends DatabaseSearch {
  isODD?: boolean;
}

export enum ScreeningConclusions {
  PEP = 'PEP',
  SANCTION = 'SANCTION',
  ADVERSE_NEWS = 'ADVERSE_NEWS',
  NO_HIT = 'NO_HIT'
}

export interface ScreeningConclusion extends BaseAuditModel {
  invalid: boolean;
  pep: boolean;
  sanction: boolean;
  adverseNews: boolean;
  noHit: boolean;
  crp: number;
}

export interface ScreeningConclusionPayload {
  customer: number;
  crp: number;
  pep: boolean;
  sanction: boolean;
  adverseNews: boolean;
  noHit: boolean;
}

export class RecordScreeningError {
  recordId: number;
  failedSearches?: Array<SearchEngineTypeValue>;
}

export interface ScreeningErrors {
  [recordId: number]: GenericError<RecordScreeningError>;
}

export interface ScreeningResultComment {
  value: string;
  createdBy?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
}
